import { useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import { selectIsContactsVisible } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/selectors";
import { setRecordToEnableExport } from "app/pages/sales-intelligence/sub-modules/management/store/actions/common";
import {
    setIntegrationModalState,
    setIsMissingMandatoryFieldsModalOpen,
    setToggle,
} from "app/pages/sales-intelligence/sub-modules/management/store/actions/export/modal";
import type { ExportName } from "app/pages/sales-intelligence/sub-modules/management/store/type";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { allTrackers } from "app/services/track/track";

import { useIsLoadingExportPreferences } from "../useIsLoadingExportPreferences";

import { getToggleProps } from "./toggleProps";

export const useExportToggleProps = (integrationName: Integration, toggleName: ExportName) => {
    const dispatch = useDispatch();

    const isContactsVisible = useSelector(selectIsContactsVisible);

    const isLocked = toggleName === "accounts" ? false : !isContactsVisible;

    const props = useMemo(() => getToggleProps(integrationName, toggleName), [integrationName, toggleName]);

    const isLoading = useIsLoadingExportPreferences();

    const { text, totalFields, onToggle, setLocalValue } = props;

    const value = useSelector(props.selectValue);
    const mappedFieldsCount = useSelector(props.selectMappedFieldsCount);
    const missingRequiredFieldsCount = useSelector(props.selectMissingRequiredFieldsCount);

    const onClick = () => {
        if (!value && missingRequiredFieldsCount > 0) {
            dispatch(setIsMissingMandatoryFieldsModalOpen(true));
            dispatch(setRecordToEnableExport(toggleName));
            dispatch(setLocalValue(true));
            return;
        }

        const isModalsDisabled = sessionStorage.getItem("isDisabledIntegrationModal");

        dispatch(setToggle(toggleName));

        if (value && !isModalsDisabled) {
            dispatch(setIntegrationModalState(true));
        } else {
            dispatch(onToggle(!value));
            const enableState = !value ? "on" : "off";
            const trackingAction = `${!value ? "disable" : "enable"} export`;

            allTrackers.dispatchTrackEvent(
                `${integrationName} integration`,
                trackingAction,
                `export/${toggleName}/${enableState}`,
            );
        }
    };

    return {
        value,
        isLoading,
        mappedFieldsCount,
        isLocked,
        text,
        totalFields,
        onClick,
        missingRequiredFieldsCount,
    };
};
